import { Link } from "react-router-dom";
import type { Article } from "@/utils/articles";
import { getCategoryName } from "@/utils/articles";
import { formatDate, formatViews } from "@/utils/format";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "horizontal" | "rank";
  rank?: number;
}

export default function ArticleCard({ article, variant = "default", rank }: ArticleCardProps) {
  const categoryName = getCategoryName(article.category);

  if (variant === "horizontal") {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex gap-4 bg-background-50 rounded-lg border border-background-200 p-3 hover:border-primary-300 transition cursor-pointer"
      >
        <div className="relative w-28 h-20 sm:w-44 sm:h-28 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.image}
            alt={article.title}
            title={`${article.title} - 黑料资讯`}
            className="w-full h-full object-cover object-top transition duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="px-2 py-0.5 rounded bg-primary-100 text-primary-700 text-xs font-medium">
              {categoryName}
            </span>
            <span className="text-xs text-foreground-400 hidden sm:inline">
              {formatDate(article.date)}
            </span>
          </div>
          <h3 className="font-heading font-bold text-sm sm:text-base leading-snug line-clamp-2 group-hover:text-primary-600 transition">
            {article.title}
          </h3>
          <p className="hidden sm:block text-sm text-foreground-600 leading-relaxed line-clamp-2 mt-1.5">
            {article.summary}
          </p>
          <div className="flex items-center gap-3 text-xs text-foreground-500 mt-auto pt-2">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line" />
              {article.author}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "rank") {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex items-center gap-3 py-3 border-b border-background-200 last:border-0 cursor-pointer"
      >
        <span
          className={`w-6 h-6 shrink-0 rounded flex items-center justify-center text-xs font-bold ${
            typeof rank === "number" && rank <= 3
              ? "bg-primary-500 text-background-50"
              : "bg-background-200 text-foreground-600"
          }`}
        >
          {rank}
        </span>
        <h3 className="font-medium text-sm leading-snug line-clamp-2 group-hover:text-primary-600 transition flex-1">
          {article.title}
        </h3>
        <span className="text-xs text-foreground-400 shrink-0 hidden sm:flex items-center gap-1">
          <i className="ri-fire-line" />
          {formatViews(article.views)}
        </span>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex flex-col bg-background-50 rounded-lg border border-background-200 overflow-hidden hover:border-primary-300 transition cursor-pointer h-full"
    >
      <div className="relative w-full aspect-[16/10] overflow-hidden bg-background-200">
        <img
          src={article.image}
          alt={article.title}
          title={`${article.title} - 黑料资讯`}
          className="w-full h-full object-cover object-top transition duration-500 group-hover:scale-105"
        />
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-primary-500 text-background-50 text-xs font-medium">
          {categoryName}
        </span>
      </div>
      <div className="flex flex-col gap-2 p-4 flex-1">
        <h3 className="font-heading font-bold text-base leading-snug line-clamp-2 group-hover:text-primary-600 transition">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">
          {article.summary}
        </p>
        <div className="flex items-center gap-3 text-xs text-foreground-500 pt-1 border-t border-background-100 mt-1">
          <span className="flex items-center gap-1">
            <i className="ri-user-line" />
            {article.author}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" />
            {formatDate(article.date)}
          </span>
        </div>
      </div>
    </Link>
  );
}